import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { IMG_TRUCK, IMG_FLEET } from "../mock";
import { useApp } from "../context/AppContext";
import { ICONS, mediaUrl } from "../api";
import { CONTACT_TEXT, waUrl } from "../waHelper";

function PageHeader({ eyebrow, title, sub }) {
  return (
    <section className="relative grid-bg border-b border-black/10 overflow-hidden">
      <div className="absolute -top-24 right-1/4 w-96 h-96 rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="max-w-7xl mx-auto px-5 py-20 relative animate-fade-up">
        <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">{eyebrow}</span>
        <h1 className="font-display font-bold text-4xl md:text-5xl mt-3">{title}</h1>
        <p className="text-neutral-600 mt-4 max-w-2xl text-lg leading-relaxed">{sub}</p>
      </div>
    </section>
  );
}

export default function Services() {
  const nav = useNavigate();
  const { settings, services, siteImages, t } = useApp();
  const truckImg = mediaUrl(siteImages.servicesTruck) || IMG_TRUCK;
  const fleetImg = mediaUrl(siteImages.servicesFleet) || IMG_FLEET;
  return (
    <div>
      <PageHeader eyebrow={t("srv.eyebrow")} title={t("srv.title")} sub={t("srv.subtitle")} />

      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s) => { const Icon = ICONS[s.icon] || ICONS["package-check"]; return (
            <div key={s.id || s.title} className="glass card-hover rounded-2xl p-7">
              <div className="w-12 h-12 rounded-xl orange-gradient grid place-items-center mb-5"><Icon className="w-6 h-6 text-white" /></div>
              <h3 className="font-display font-semibold text-lg">{s.title}</h3>
              <p className="text-neutral-600 text-sm mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ); })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-10 grid lg:grid-cols-2 gap-14 items-center">
        <div className="rounded-3xl overflow-hidden glass p-2"><img src={truckImg} alt="Road transport" className="rounded-2xl w-full h-[380px] object-cover" /></div>
        <div>
          <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider">Why SmartLogistics</span>
          <h2 className="font-display font-bold text-3xl md:text-4xl mt-3">A smarter way to ship</h2>
          <ul className="mt-6 space-y-4">
            {["True door-to-door — no drop-off points, ever","Insured parcels with live WhatsApp updates","Transparent pricing with instant quotes","Dedicated corridors to Asia, UK & across Europe","Friendly multilingual support, 24/7"].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full orange-gradient grid place-items-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" strokeWidth={3} /></div>
                <span className="text-neutral-700">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button className="orange-gradient text-white border-0 gap-2 hover:opacity-90" onClick={() => nav("/send")}>Send a Parcel <ArrowRight className="w-4 h-4" /></Button>
            <a href={waUrl(settings.whatsapp, CONTACT_TEXT)} target="_blank" rel="noreferrer"><Button variant="outline" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-2"><MessageCircle className="w-4 h-4 text-orange-500" /> Ask a question</Button></a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="relative rounded-3xl overflow-hidden glass">
          <img src={fleetImg} alt="Fleet" className="w-full h-72 object-cover opacity-40" />
          <div className="absolute inset-0 grid place-items-center text-center px-6">
            <div>
              <h2 className="font-display font-bold text-3xl md:text-4xl">One partner, every mile</h2>
              <p className="text-neutral-700 mt-3 max-w-xl mx-auto">Air, sea and road — combined into a single seamless door-to-door experience.</p>
              <Button className="orange-gradient text-white border-0 mt-6 gap-2 hover:opacity-90" onClick={() => nav("/coverage")}>See our coverage <ArrowRight className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
